import { createContext } from 'react';
import {
	TFileId,
	IItemForGetShortInfo,
	TLanguageIDs,
	IMenuTranslation,
	IDefaultLanguage,
	ICreateDishV2,
	IItemForGetShortDishInfo,
} from 'types';

export type TTypeOfModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'success'
	| 'error'
	| 'defaultTranslationIsEmpty';

export interface ICreateDishState {
	fileId: TFileId;
	nameOfDish: string;
	description: string;
	fetchedCustomers: IItemForGetShortInfo[];
	IDSelectedCustomer: Nullable<string>;
	externalId: string;
	defaultCustomerLanguage: Nullable<IDefaultLanguage>;
	selectedTranslationTab: Nullable<TLanguageIDs>;
	translations: IMenuTranslation[];
	hasSchedulePriority: boolean;
	activationSchedule: string;
	deactivationSchedule: string;
	fetchedRestaurants: IItemForGetShortInfo[];
	IDsSelectedRestaurant: string[];
	fetchedCategories: IItemForGetShortInfo[];
	selectedSuggestedDishCategory: Nullable<string>;
	fetchedSuggestedDishSubCategories: IItemForGetShortInfo[];
	selectedSuggestedDishSubCategory: Nullable<string>;
	fetchedSuggestedDishes: IItemForGetShortDishInfo[];
	selectedSuggestedDishes: string[];
	selectedAdditionalDishCategory: Nullable<string>;
	fetchedAdditionalDishSubCategories: IItemForGetShortInfo[];
	selectedAdditionalDishSubCategory: Nullable<string>;
	fetchedAdditionalDishes: IItemForGetShortDishInfo[];
	selectedAdditionalDishes: string[];
	selectedModificationDishCategory: Nullable<string>;
	fetchedModificationDishSubCategories: IItemForGetShortInfo[];
	selectedModificationDishSubCategory: Nullable<string>;
	fetchedModificationDishes: IItemForGetShortDishInfo[];
	selectedModificationDishes: string[];
	dataForSubmit: Nullable<ICreateDishV2>;
	typeOfModalWindow: TTypeOfModalWindow;
}

export const createDishInitialState: ICreateDishState = {
	fileId: null,
	nameOfDish: '',
	description: '',
	fetchedCustomers: [],
	IDSelectedCustomer: null,
	externalId: '',
	defaultCustomerLanguage: null,
	selectedTranslationTab: null,
	translations: [],
	hasSchedulePriority: false,
	activationSchedule: '',
	deactivationSchedule: '',
	fetchedRestaurants: [],
	IDsSelectedRestaurant: [],
	fetchedCategories: [],
	selectedSuggestedDishCategory: null,
	fetchedSuggestedDishSubCategories: [],
	selectedSuggestedDishSubCategory: null,
	fetchedSuggestedDishes: [],
	selectedSuggestedDishes: [],
	selectedAdditionalDishCategory: null,
	fetchedAdditionalDishSubCategories: [],
	selectedAdditionalDishSubCategory: null,
	fetchedAdditionalDishes: [],
	selectedAdditionalDishes: [],
	selectedModificationDishCategory: null,
	fetchedModificationDishSubCategories: [],
	selectedModificationDishSubCategory: null,
	fetchedModificationDishes: [],
	selectedModificationDishes: [],
	dataForSubmit: null,
	typeOfModalWindow: null,
};

export interface ICreateDishContext extends ICreateDishState {
	setFileId(value: TFileId): void;
	changeNameOfDish(value: string): void;
	changeDescription(value: string): void;
	setFetchedCustomers(value: IItemForGetShortInfo[]): void;
	setIDCustomer(value: Nullable<string>): void;
	changeExternalId(value: string): void;
	generateUniqueExternalId(): void;
	setFetchedTranslations(value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>): void;
	setTranslationTab(value: Nullable<TLanguageIDs>): void;
	changeNameTranslation(languageId: TLanguageIDs, value: string): void;
	changeDescriptionTranslation(languageId: TLanguageIDs, value: string): void;
	toggleHasSchedulePriority(): void;
	changeActivationSchedule(value: string): void;
	changeDeactivationSchedule(value: string): void;
	setFetchedRestaurants(value: IItemForGetShortInfo[]): void;
	setIDsRestaurant(value: string[]): void;
	setFetchedCategories(value: IItemForGetShortInfo[]): void;
	setSuggestedDishCategory(value: Nullable<string>): void;
	setFetchedSuggestedDishSubCategories(value: IItemForGetShortInfo[]): void;
	setSuggestedDishSubCategory(value: Nullable<string>): void;
	setFetchedSuggestedDishes(value: IItemForGetShortInfo[]): void;
	setSuggestedDishes(value: string[]): void;
	removeSuggestedDish(value: string): void;
	setAdditionalDishCategory(value: Nullable<string>): void;
	setFetchedAdditionalDishSubCategories(value: IItemForGetShortInfo[]): void;
	setAdditionalDishSubCategory(value: Nullable<string>): void;
	setFetchedAdditionalDishes(value: IItemForGetShortInfo[]): void;
	setAdditionalDishes(value: string[]): void;
	removeAdditionalDish(value: string): void;
	setModificationDishCategory(value: Nullable<string>): void;
	setFetchedModificationDishSubCategories(value: IItemForGetShortInfo[]): void;
	setModificationDishSubCategory(value: Nullable<string>): void;
	setFetchedModificationDishes(value: IItemForGetShortInfo[]): void;
	setModificationDishes(value: string[]): void;
	removeModificationDish(value: string): void;
	setDataForSubmit(data: ICreateDishV2): void;
	changeTypeOfModalWindow(value: TTypeOfModalWindow): void;
	resetFields(): void;
}

export const CreateDishContext = createContext<ICreateDishContext>({
	setFileId: () => undefined,
	changeNameOfDish: () => undefined,
	changeDescription: () => undefined,
	setFetchedCustomers: () => undefined,
	setIDCustomer: () => undefined,
	changeExternalId: () => undefined,
	generateUniqueExternalId: () => undefined,
	setFetchedTranslations: () => undefined,
	setTranslationTab: () => undefined,
	changeNameTranslation: () => undefined,
	changeDescriptionTranslation: () => undefined,
	toggleHasSchedulePriority: () => undefined,
	changeActivationSchedule: () => undefined,
	changeDeactivationSchedule: () => undefined,
	setFetchedRestaurants: () => undefined,
	setIDsRestaurant: () => undefined,
	setFetchedCategories: () => undefined,
	setSuggestedDishCategory: () => undefined,
	setFetchedSuggestedDishSubCategories: () => undefined,
	setSuggestedDishSubCategory: () => undefined,
	setFetchedSuggestedDishes: () => undefined,
	setSuggestedDishes: () => undefined,
	removeSuggestedDish: () => undefined,
	setAdditionalDishCategory: () => undefined,
	setFetchedAdditionalDishSubCategories: () => undefined,
	setAdditionalDishSubCategory: () => undefined,
	setFetchedAdditionalDishes: () => undefined,
	setAdditionalDishes: () => undefined,
	removeAdditionalDish: () => undefined,
	setModificationDishCategory: () => undefined,
	setFetchedModificationDishSubCategories: () => undefined,
	setModificationDishSubCategory: () => undefined,
	setFetchedModificationDishes: () => undefined,
	setModificationDishes: () => undefined,
	removeModificationDish: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	resetFields: () => undefined,
	...createDishInitialState,
});
